import { useState, useMemo, useRef, useCallback } from 'react'
import { AutoComplete, Spin } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import debounce from 'lodash/debounce'
import { ArticleColumn } from '../../../Constants/TableConfiguration'
import { saveFilters, saveFilterTags, setAutocompleteValues } from '../../../Slices/Kiosk/TableOptionsSlice'
import {
  populateTableWithArticles,
  populateTableAndSelectFirstRowOnSearch,
} from '../../../Services/Kiosk/ArticleServices'
import { getAutocompleteResults } from '../../../Services/Kiosk/AutocompleteService'
import {
  setSubRuleAutocompleteValues,
  saveSubruleFilters,
  saveSubruleFilterTags,
} from '../../../Slices/Omnichannel/SubRuleSlice'
import { populateSubRuleTableWithArticles } from '../../../Services/Omnichannel/SeasonRuleService'
import { getHtmlTableAutocompleteParentElement, handleErrorCatchLog } from '../../../Helpers/GenericHelpers'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { ITag, searchTags } from 'Services/Kiosk/TagsService'

function TitleAutocomplete(props) {
  const dispatch = useAppDispatch()
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const autocompleteValues = useAppSelector(state =>
    props.isFromDiWa ? state.present.tableOptions.autocompleteValues : state.present.subRule.autocompleteValues,
  )

  const autocompleteText = useMemo(() => {
    return autocompleteValues[props.label]
  }, [autocompleteValues, props.label])

  const [options, setOptions] = useState([])
  const [fetching, setFetching] = useState(false)
  const [tagOptions, setTagOptions] = useState<ITag[]>()
  const fetchRef = useRef(0)

  const onSelect = useCallback(
    data => {
      if (props.isFromDiWa) {
        dispatch(saveFilters({ key: props.label, value: data }))
        if (props.label === ArticleColumn.ArticleCode) {
          populateTableAndSelectFirstRowOnSearch(dispatch, editTableDisable)
          return
        }
        if (props.label === ArticleColumn.Tag) {
          dispatch(saveFilterTags(tagOptions.find(x => x.slug === data)))
        }
        dispatch(populateTableWithArticles())
      } else {
        dispatch(saveSubruleFilters({ key: props.label, value: data }))
        dispatch(populateSubRuleTableWithArticles(false))
        if (props.label === ArticleColumn.Tag) {
          dispatch(saveSubruleFilterTags(tagOptions.find(x => x.slug === data)))
        }
      }
    },
    [dispatch, editTableDisable, props.isFromDiWa, props.label, tagOptions],
  )

  const onChange = useCallback(
    data => {
      if (props.isFromDiWa) {
        dispatch(setAutocompleteValues({ label: props.label, text: data }))
      } else {
        dispatch(setSubRuleAutocompleteValues({ label: props.label, text: data }))
      }
    },
    [dispatch, props.isFromDiWa, props.label],
  )

  const barcodeCheck = useCallback(
    e => {
      if (e.key === 'Enter') {
        //checkBarcode
        let barcode = false
        if (barcode) {
          props.onSelect()
          e.stopPropagation()
        }
        //if activeAutocomplete reset, barcode method onBarcodesearch always fire and barcode open
        setTimeout(props.onSelect, 100)
      }
    },
    [props],
  )

  const handleClick = useCallback(
    e => {
      e.stopPropagation()
      if (e.target && e.target.className === 'ant-select-item-option-content') {
        props.onSelect()
      }
    },
    [props],
  )

  const debounceFetcher = useMemo(() => {
    const loadOptions = text => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      if (options.length !== 0) {
        setOptions([])
        if (props.label === ArticleColumn.Tag) {
          setTagOptions([])
        }
      }
      setFetching(true)
      const textLenght = props.label !== ArticleColumn.Color && props.label !== ArticleColumn.ProductDescription ? 0 : 1
      if (text && text.length > textLenght) {
        if (props.label === ArticleColumn.Tag) {
          searchTags(text).then((data: ITag[]) => {
            if (fetchId !== fetchRef.current) {
              return
            }
            let result = data.map((item, index) => {
              const label = item?.slug.toLowerCase().includes(text.toLowerCase()) ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: item?.slug.replace(
                      new RegExp(`(${text})`, 'i'),
                      '<b class="highlighted-autocomplete">$1</b>',
                    ),
                  }}
                />
              ) : (
                item?.slug
              )
              return { key: index, value: item?.slug, label: label }
            })
            setOptions(result)
            setTagOptions(data)
            setFetching(false)
          })
        } else {
          getAutocompleteResults(props.label, text)
            .then((data: any) => {
              if (fetchId !== fetchRef.current) {
                return
              }
              let result = data.map((item, index) => {
                const label = item.toLowerCase().includes(text.toLowerCase()) ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: item.replace(new RegExp(`(${text})`, 'i'), '<b class="highlighted-autocomplete">$1</b>'),
                    }}
                  />
                ) : (
                  item
                )
                return { key: index, value: item, label: label }
              })
              if (
                props.label === ArticleColumn.ArticleCode ||
                props.label === ArticleColumn.Brand ||
                props.label === ArticleColumn.SubCategory
              ) {
                result.unshift({ key: -1, value: `${text}%`, label: `${text}%` })
              }

              setOptions(result)
              setFetching(false)
            })
            .catch(err => {
              handleErrorCatchLog(err, 'Error on article search autocomplete getAutocomplete')
            })
        }
      } else {
        setFetching(false)
      }
    }

    return debounce(loadOptions, 800)
  }, [options.length, props.label])

  return (
    <AutoComplete
      style={props.style}
      placeholder={
        <div>
          {props.title}{' '}
          <EditOutlined style={props.label === ArticleColumn.Size ? { paddingLeft: 2 } : { paddingLeft: 5 }} />
        </div>
      }
      onSearch={debounceFetcher}
      onSelect={onSelect}
      onChange={onChange}
      options={options}
      value={autocompleteText}
      className={props.label === ArticleColumn.Size ? 'size-autocomplete' : 'autocomplete'}
      popupClassName='exclude-modal-click'
      onClick={handleClick}
      popupMatchSelectWidth={false}
      defaultActiveFirstOption={true}
      autoFocus={true}
      onInputKeyDown={e => {
        if (props.isFromDiWa) {
          barcodeCheck(e)
        }

        if (e.key === 'Enter') {
          if (props.isFromDiWa) {
            dispatch(setAutocompleteValues({ label: props.label, text: '' }))
          } else {
            dispatch(setSubRuleAutocompleteValues({ label: props.label, text: '' }))
          }
        }
      }}
      tabIndex={0}
      notFoundContent={fetching ? <Spin size='small' /> : null}
      getPopupContainer={getHtmlTableAutocompleteParentElement}
      aria-multiselectable={true}
    />
  )
}

export default TitleAutocomplete
